/**
 * Shared date formatters used across admin features. Single source of truth for
 * pickup-date rendering, API date payloads, and human-readable date-time labels.
 *
 * `formatPickupDate` supports two parsing semantics:
 *  - `parseAs: 'local'` (default): treats `YYYY-MM-DD` as local midnight. Right
 *    for user-entered pickup windows where "Jan 5" means Jan 5 in the
 *    dispatcher's locale (agentic-hub callers).
 *  - `parseAs: 'utc'`: treats `YYYY-MM-DD` as UTC midnight and reads UTC parts.
 *    Right for upstream sources that store pickup as UTC midnight (Parade /
 *    HappyRobot in load-hub) — local parsing would shift the date back a day
 *    for negative-offset timezones.
 */

import { MONTHS_SHORT } from '@/constants/months'

type PickupDateFormat = 'MM/DD' | 'MM/DD/YYYY' | 'MMM D'

interface FormatPickupDateOptions {
    format?: PickupDateFormat
    parseAs?: 'local' | 'utc'
    /**
     * Behavior when `earliest` is a non-empty string that can't be parsed.
     *  - `'raw'` (default): return the input verbatim. Right for agentic-hub —
     *    surfaces noisy upstream data so it's visible rather than silently
     *    dropped.
     *  - `'empty'`: return `''`. Right for load-hub, where Parade/HappyRobot
     *    rows with bad timestamps should render as blank cells, not as the
     *    raw garbled string.
     */
    onInvalid?: 'raw' | 'empty'
    /** Defaults to the established spaced hyphen; compact summaries can request an en dash. */
    rangeSeparator?: string
}

const pad2 = (n: number): string => String(n).padStart(2, '0')

const parseLocalYMD = (raw: string): Date | null => {
    const [y, m, d] = raw.substring(0, 10).split('-').map(Number)
    if (!y || !m || !d) return null
    const date = new Date(y, m - 1, d)
    return isNaN(date.getTime()) ? null : date
}

const toDate = (input: Date | string, parseAs: 'local' | 'utc'): Date | null => {
    if (input instanceof Date) return isNaN(input.getTime()) ? null : input
    if (parseAs === 'local') {
        const ymdOnly = /^\d{4}-\d{2}-\d{2}$/.test(input)
        if (ymdOnly) return parseLocalYMD(input)
        // ISO with time component — still anchor to the local date portion to
        // match dispatcher mental model. parseLocalYMD slices the first 10 chars.
        if (/^\d{4}-\d{2}-\d{2}T/.test(input)) return parseLocalYMD(input)
    }
    const d = new Date(input)
    return isNaN(d.getTime()) ? null : d
}

const formatPart = (date: Date, parseAs: 'local' | 'utc', format: PickupDateFormat): string => {
    const monthIndex = parseAs === 'utc' ? date.getUTCMonth() : date.getMonth()
    const day = pad2(parseAs === 'utc' ? date.getUTCDate() : date.getDate())
    if (format === 'MMM D') return `${MONTHS_SHORT[monthIndex]} ${Number(day)}`
    const month = pad2(monthIndex + 1)
    if (format === 'MM/DD') return `${month}/${day}`
    const year = parseAs === 'utc' ? date.getUTCFullYear() : date.getFullYear()
    return `${month}/${day}/${year}`
}

/**
 * Format a pickup window for display. Accepts a single date or an
 * `earliest`/`latest` range. When `latest` equals or is missing/invalid, the
 * single-date form is returned.
 *
 * Empty `earliest` (null / undefined / "") returns `''`. Unparseable but
 * non-empty strings are routed through `opts.onInvalid` — see option doc.
 */
export const formatPickupDate = (earliest: Date | string | null | undefined, latest?: Date | string | null, opts: FormatPickupDateOptions = {}): string => {
    if (earliest === null || earliest === undefined || earliest === '') return ''
    const { format = 'MM/DD', parseAs = 'local', onInvalid = 'raw', rangeSeparator = ' - ' } = opts

    const earliestDate = toDate(earliest, parseAs)
    if (!earliestDate) {
        if (onInvalid === 'empty') return ''
        return typeof earliest === 'string' ? earliest : ''
    }

    const latestDate = latest ? toDate(latest, parseAs) : null
    const head = formatPart(earliestDate, parseAs, format)

    if (latestDate && latestDate.getTime() !== earliestDate.getTime()) {
        const tail = formatPart(latestDate, parseAs, format)
        return `${head}${rangeSeparator}${tail}`
    }
    return head
}

/**
 * The US freight market's clock. Pickup dates are US dates: a load posted for "today" is today in the
 * MARKET, not in whatever timezone the dispatcher happens to sit in. Pacific is the last continental US
 * zone to roll over, so it is the one that decides how long a US calendar day is still live.
 */
const US_MARKET_TIME_ZONE = 'America/Los_Angeles'

/** Local midnight of the calendar day `timeZone` is currently on. */
const todayInZone = (timeZone: string): Date => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date())
    const part = (type: Intl.DateTimeFormatPartTypes): number => Number(parts.find((p) => p.type === type)?.value)
    return new Date(part('year'), part('month') - 1, part('day'))
}

/**
 * The earliest calendar day still "today" for the US market, as a LOCAL midnight Date — what a pickup
 * picker must treat as its minimum, NOT the browser's own today.
 *
 * A dispatcher in Tashkent (UTC+5) rolls into Sep 1 while Los Angeles is still on Aug 31 — and Aug 31
 * loads are still being posted and booked. Keying "past" off the browser greyed that day out and dropped
 * it from the default window, hiding same-day US freight from every dispatcher ahead of the market.
 *
 * Takes the EARLIER of the browser's today and the market's today, so a dispatcher BEHIND Pacific
 * (Hawaii, Alaska) never loses their own today either.
 */
export const getMarketToday = (): Date => {
    const local = new Date()
    local.setHours(0, 0, 0, 0)
    const market = todayInZone(US_MARKET_TIME_ZONE)
    return market < local ? market : local
}

/** Format date as MM/DD/YYYY for API requests (local time). */
export const formatDateForApi = (date: Date): string => `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}/${date.getFullYear()}`

/** Format date as YYYY-MM-DD (for Arrive/Navisphere APIs, local time). */
export const formatDateYMD = (date: Date): string => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`

/** Year, month (1-12) and day of a `YYYY-MM-DD` string or the date part of an ISO timestamp, or null when it is not one. */
const ymdParts = (value: string | null | undefined): [number, number, number] | null => {
    const match = value ? /^(\d{4})-(\d{2})-(\d{2})/.exec(value) : null
    return match ? [Number(match[1]), Number(match[2]), Number(match[3])] : null
}

/** The UTC calendar day of `date` as YYYY-MM-DD. For dates stored as UTC midnight (e.g. a driver's CDL expiry). Inverse of `parseDateUTCYMD`. */
export const formatDateUTCYMD = (date: Date): string => date.toISOString().slice(0, 10)

/** The UTC-midnight Date of a `YYYY-MM-DD` string, or null when empty or not a date. Inverse of `formatDateUTCYMD`. */
export const parseDateUTCYMD = (value: string | null | undefined): Date | null => {
    const parts = ymdParts(value)
    return parts ? new Date(Date.UTC(parts[0], parts[1] - 1, parts[2])) : null
}

/** The local-midnight Date of a `YYYY-MM-DD` string (or the date part of an ISO timestamp), or null when empty or not a date. Inverse of `formatDateYMD`. */
export const parseDateYMD = (value: string | null | undefined): Date | null => {
    const parts = ymdParts(value)
    return parts ? new Date(parts[0], parts[1] - 1, parts[2]) : null
}

/**
 * Encode the local-zone midnight of `date` as a UTC ISO 8601 timestamp.
 * Mirrors what loadboard UIs (TQL, DAT) ship for date filters: the user picked
 * "Jun 11" in their wall clock and the upstream receives the instant that
 * corresponds to Jun 11 00:00 in *their* timezone. Use this for adapters whose
 * upstream interprets `LoadDate` in the caller's local TZ; for simple
 * date-only APIs prefer `formatDateYMD` / `formatDateForApi`.
 */
export const formatDateAsLocalIso = (date: Date): string => new Date(date.getFullYear(), date.getMonth(), date.getDate()).toISOString()

/** A calendar date with its year: "Oct 14, 2026". */
export const formatMonthDayYear = (date: Date | string | number): string =>
    new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/**
 * Long-form date-time label: "Tuesday, March 10, 2:30 PM". Used by
 * agentic-hub LoadRowSummary for the pickup tooltip. Returns `'-'` for empty
 * input and the raw string for unparseable input.
 */
export const formatDateTime = (dateStr: string | Date | undefined | null): string => {
    if (dateStr === null || dateStr === undefined || dateStr === '') return '-'
    try {
        const d = dateStr instanceof Date ? dateStr : new Date(dateStr)
        if (isNaN(d.getTime())) return typeof dateStr === 'string' ? dateStr : '-'
        return d.toLocaleString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, weekday: 'long', month: 'long', day: 'numeric' })
    } catch {
        return typeof dateStr === 'string' ? dateStr : '-'
    }
}

/**
 * Format an instant in US Eastern — the zone the Sylectus loadboard renders its post/expiry
 * times in. `MM/DD, HH:MM` by default; `withYear` adds the year, `withZone` appends " ET".
 * Returns null for empty/invalid input. Single source of truth for Sylectus ET labels (load
 * age column + expiry row).
 */
export const formatEasternDateTime = (value: string | Date | null | undefined, opts: { withYear?: boolean; withZone?: boolean } = {}): string | null => {
    if (!value) return null
    const d = value instanceof Date ? value : new Date(value)
    if (isNaN(d.getTime())) return null
    const formatted = easternFormatter(opts.withYear === true).format(d)
    return opts.withZone ? `${formatted} ET` : formatted
}

/**
 * The two Eastern formatters this function can need (with and without the year), built on first use and kept.
 *
 * `toLocaleString` constructs an `Intl.DateTimeFormat` on every call and that construction is the expensive
 * part by a wide margin — ~70 µs against ~2 µs to reuse one. This runs per row on a virtualized loads table
 * (the Sylectus exact-time age), so a screenful used to be milliseconds of formatter construction per render.
 * Same locale, same options, same output.
 */
const easternFormatters = new Map<boolean, Intl.DateTimeFormat>()

const easternFormatter = (withYear: boolean): Intl.DateTimeFormat => {
    const cached = easternFormatters.get(withYear)
    if (cached) return cached
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
        ...(withYear ? { year: 'numeric' as const } : {}),
    })
    easternFormatters.set(withYear, formatter)
    return formatter
}

/**
 * Format a Unix timestamp (ms) as a full date-time string with timezone.
 * Used by download-center to display completion times.
 * Example: "05/26/2026, 03:14:22 AM PDT"
 */
export const formatTimestampWithZone = (ts: number): string => {
    const d = new Date(ts)
    return d.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZoneName: 'short',
    })
}
